export type TourPlacement = 'top' | 'bottom' | 'bottom-start' | 'left' | 'right';
export type TourIntent = 'openNotifications' | 'closeNotifications' | 'openMobileNav' | 'closeMobileNav';

export type ProductTourStep = {
  id: string;
  title: string;
  body: string;
  route: string;
  routeFallbacks?: string[];
  target: string;
  targetFallback?: string;
  kind: 'control' | 'region';
  placement: TourPlacement;
  requires?: string[];
  prepare?: TourIntent;
  cleanup?: TourIntent;
  interactive?: boolean;
};

// Any-of semantics, and legacy role keys are listed alongside catalogue keys so
// built-in roles are not filtered out of their own orientation.
export const PRODUCT_TOUR_STEPS: ProductTourStep[] = [
  {
    id: 'project-context',
    title: 'Project context',
    body: 'Everything you see — records, dashboards and reports — is scoped to this project. Switch projects here when you need to work on another register.',
    route: '/dashboard',
    routeFallbacks: ['/consents', '/help'],
    target: 'project-selector',
    kind: 'control',
    placement: 'bottom',
  },
  {
    id: 'navigation',
    title: 'Finding your way around',
    body: 'Navigation is grouped by project work, regulatory intelligence, activity and administration. You only see the areas your access allows.',
    route: '/dashboard',
    routeFallbacks: ['/consents', '/help'],
    target: 'main-navigation',
    targetFallback: 'nav-trigger',
    kind: 'region',
    placement: 'right',
    prepare: 'openMobileNav',
    cleanup: 'closeMobileNav',
  },
  {
    id: 'consent-register',
    title: 'The consent register',
    body: 'This is the main workspace: every consent, its owner, its lifecycle position and its evidence. Filter the register, then open a consent to work on it.',
    route: '/consents',
    target: 'consent-register',
    kind: 'region',
    placement: 'top',
    requires: ['consent.view'],
  },
  {
    id: 'attention',
    title: 'What needs your attention',
    body: 'Approaching deadlines, review requests and source events collect here. The badge tells you when something is waiting on you.',
    route: '/consents',
    routeFallbacks: ['/dashboard', '/help'],
    target: 'notifications-panel',
    targetFallback: 'notifications-trigger',
    kind: 'region',
    placement: 'left',
    prepare: 'openNotifications',
    cleanup: 'closeNotifications',
    requires: ['notification.view', 'consent.view'],
  },
  {
    id: 'help',
    title: 'Help whenever you need it',
    body: 'Step-by-step guides for every workflow live here, tailored to your permissions. Start with the end-to-end consent walkthrough.',
    route: '/help',
    target: 'help-launcher',
    kind: 'region',
    placement: 'bottom',
  },
];

export const ROUTE_REQUIRES: Record<string, string[]> = {
  '/dashboard': ['dashboard.view', 'project.view', 'consent.view'],
  '/consents': ['consent.view'],
  '/help': [],
};

export const TOUR_COMPLETION_ROUTE = '/help?article=first-consent-workflow';
